/**
 *
 * FBAppointmentsPlan
 *
 */

import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Button, Tooltip } from "antd";

// layout components
import ErrorBoundary from "engine/ErrorBoundary";

// icons
import { StarFilled } from "@ant-design/icons";

// custom react-multi-date-picker
import { DateObject } from "react-multi-date-picker";
import { timeFormat, dateFormat } from "utils/moment/moment.constant";

// utils
import { getObjId } from "utils/helpers/helper.general";

// hooks
// import { useRefState } from 'utils/hooks';

function FBAppointmentsPlan({
  type,
  values,
  // actions
  onUpdate,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    "tcf-fb-appointments-group-preview",
    "tcf-fb-group-preview",
    "tcf-fb-group-answer-preview",
    className,
  );
  const { t, i18n } = useTranslation();

  const init = {
    columnHeaders: [],
    rowCell: [],
  };

  const [generatedData, setGeneratedData] = useState(null);
  const [currentResult, setCurrentResult] = useState([]);

  const [savedResult, setSavedResult] = useState([]);
  const [planStatus, setPlanStatus] = useState("save");

  useEffect(() => {
    setCurrentResult([]);
    setSavedResult([]);
    setPlanStatus("save");
    setGeneratedData(null);
    return () => {
      setCurrentResult([]);
      setSavedResult([]);
      setPlanStatus("save");
      setGeneratedData(null);
    }
  }, []);

  // Refresh current value based on parent`s states
  useEffect(() => {
    if (!!values) {
      const appointmentsPlan = values?.[0]?.appointmentsPlan;
      // Has Saved Plan Data
      if (!!appointmentsPlan?.plan) {
        setCurrentResult([...appointmentsPlan?.plan]);
        setPlanStatus(appointmentsPlan?.status);
      }
      generateAppointmentData(values);
    } else if (!!!values) {
      setGeneratedData({});
    }
  }, [values]);

  // Refresh current value based on parent`s states
  /*   useEffect(() => {
    if (savedResult?.length > 0) {
      setCurrentResult(savedResult);
    }
  }, [savedResult]); */

  // ReUpdate Plan after saving results
  useEffect(() => {
    if (!!generatedData) {
      const rowCell = reUpdateBody(currentResult);
      const columnHeaders = reUpdateHeaders(currentResult, rowCell);
      setGeneratedData(prev => ({
        ...prev,
        columnHeaders,
        rowCell,
      }));
      !!onUpdate && onUpdate(currentResult);
    }
  }, [currentResult]);

  const formatDateTimeValue = useCallback((value, format) => {
    if (!!!value) {
      return "";
    }

    if (typeof value == "object") {
      return !!value && value.format(format);
    } else {
      return !!value && new DateObject(value).format(format);
    }
  }, []);

  // Initial Generation of Plan
  const generateAppointmentData = useCallback(
    allData => {
      let resultTable = {
        columnHeaders: [],
        rowCell: [],
      };
      let columnHeaders = [];
      let rowCell = [];
      let rowAdminAnswers = currentResult || [];

      const getAdminAnswers = (item, i) => {
        const stdAdminAnswer = item?.adminAnswer;
        if (
          !!stdAdminAnswer &&
          typeof stdAdminAnswer == "object" &&
          Object.keys(stdAdminAnswer)?.length > 0
        ) {
          rowAdminAnswers = [
            ...new Set([...rowAdminAnswers, stdAdminAnswer]),
          ];
        }
      };

      const generateRowCell = (columnHeaders, item, i) => {
        let reservedAmount = 0;
        let rowData = {
          slots: [],
          reservedAmount: reservedAmount,
        };
        let slots = [];
        const stdAnswer = item?.answer?.[type];
        const stdAnswerList =
          (!!stdAnswer && Object.values(stdAnswer?.data)) || [];

        // std name surname
        if (item?.studentData && item?.studentData?.length > 0) {
          rowData = {
            ...rowData,
            firstname: item?.studentData[0]?.description?.firstname,
            lastname: item?.studentData[0]?.description?.lastname,
            student: getObjId(item?.student),
          };
        }

        // generate answers
        if ( Array.isArray(stdAnswerList) && stdAnswerList?.length > 0 ) {
          rowData.answeredRecipient = getObjId(stdAnswer?.recipient);
          rowData.responseID = getObjId(stdAnswer?.responseID);

          const colHeaders = columnHeaders?.map((timeSlot, slotInx) => {
            const prepearSlot = `${timeSlot?.timeFrom} - ${timeSlot?.timeTo}`;
            let { selected, selectedAmount, ...restTimeSlot } = timeSlot;
            let tmpSelectedAmount = selectedAmount;
            let coreSlot = {
              ...restTimeSlot,
              reserved: false,
            };
            const findAnswer = stdAnswerList?.find(
              answ => answ?.["field-date"] == timeSlot?.date,
            );

            // check if we had answer on this date
            if (!!findAnswer) {
              const fieldAnswer = findAnswer[`${findAnswer["field-type"]}-answer`];

              // check if we had answer on this time
              if (findAnswer["field-type"] == "radio") {
                if (fieldAnswer == prepearSlot) {
                  reservedAmount =
                    reservedAmount < columnHeaders?.length
                      ? reservedAmount + 1
                      : 0;
                  tmpSelectedAmount =
                    tmpSelectedAmount > 0
                      ? tmpSelectedAmount - 1
                      : tmpSelectedAmount;
                  const slot = {
                    ...coreSlot,
                    reserved: true,
                  };

                  const hasSlotIndex = slots.findIndex(
                    ss =>
                      ss?.date == timeSlot?.date &&
                      ss?.timeFrom == timeSlot?.timeFrom,
                  );
                  if (hasSlotIndex == -1) {
                    slots.push(slot);
                  } else {
                    slots = slots?.map((el, ix) =>
                      ix == hasSlotIndex ? slot : el,
                    );
                  }
                } else {
                  const hasSlotIndex = slots.findIndex(
                    ss =>
                      ss?.date == timeSlot?.date &&
                      ss?.timeFrom == timeSlot?.timeFrom,
                  );
                  if (hasSlotIndex == -1) {
                    slots.push(coreSlot);
                  } else {
                    slots = slots?.map((el, ix) =>
                      ix == hasSlotIndex ? coreSlot : el,
                    );
                  }
                }
              } else if (
                findAnswer["field-type"] == "checkbox" &&
                Array.isArray(fieldAnswer)
              ) {
                // check if we had answer on this time array
                if (fieldAnswer?.length > 0) {

                  for(const filed of fieldAnswer) {
                    const hasSlotIndex = slots.findIndex(
                      ss => `${ss?.timeFrom} - ${ss?.timeTo}` == prepearSlot,
                    );
                    if (filed == prepearSlot) {
                      reservedAmount =
                        reservedAmount < columnHeaders?.length
                          ? reservedAmount + 1
                          : 0;
                      tmpSelectedAmount =
                        tmpSelectedAmount > 0
                          ? tmpSelectedAmount - 1
                          : tmpSelectedAmount;
                      const slot = {
                        ...coreSlot,
                        reserved: true,
                      };
  
                      if (hasSlotIndex == -1) {
                        slots.push(slot);
                      } else {
                        slots = slots?.map((el, ix) =>
                          ix == hasSlotIndex ? slot : el,
                        );
                      }
                    } else {
                      if (hasSlotIndex == -1) {
                        slots.push(coreSlot);
                      }
                    }
                  }

                } else {
                  const hasSlotIndex = slots.findIndex(
                    ss =>
                      ss?.date == timeSlot?.date &&
                      ss?.timeFrom == timeSlot?.timeFrom,
                  );

                  if (hasSlotIndex == -1) {
                    slots.push(coreSlot);
                  } 
                }
              } else {
                const hasSlotIndex = slots.findIndex(
                  ss =>
                    ss?.date == timeSlot?.date &&
                    ss?.timeFrom == timeSlot?.timeFrom,
                );
                if (hasSlotIndex == -1) {
                  slots.push(coreSlot);
                }
              }
            } else {
              const hasSlotIndex = slots.findIndex(
                ss =>
                  ss?.date == timeSlot?.date &&
                  ss?.timeFrom == timeSlot?.timeFrom,
              );

              if (hasSlotIndex == -1) {
                slots.push(coreSlot);
              }
            }

            const hasAdminAnswer = rowAdminAnswers.find(
              adminAnswer =>
                adminAnswer?.slot?.date == timeSlot?.date &&
                adminAnswer?.slot?.timeFrom == timeSlot?.timeFrom &&
                adminAnswer?.slot?.timeTo == timeSlot?.timeTo,
            );

            return {
              ...timeSlot,
              selected: !!hasAdminAnswer,
              selectedAmount: tmpSelectedAmount,
            };
          });

          columnHeaders = colHeaders;
        }

        rowCell.push({ ...rowData, reservedAmount, slots });
      }

      if (!!allData && Array.isArray(allData) && allData?.length > 0) {
        const appointments = allData?.[0]?.[type][`${type}-fields`];

        // generate Column Headers
        let amountOfHeaders = 0;
        appointments?.map((el, dateindx) => {
          const date = el?.["field-date"];
          const formatDate = !!date && formatDateTimeValue(date, dateFormat);
          const timeSlot = el?.["field-input"];
          if (!!timeSlot && Array.isArray(timeSlot) && timeSlot?.length > 0) {
            timeSlot?.map((time, timeindx) => {
              const timeFrom = time?.[`time-from`],
                timeFromFormat =
                  !!timeFrom && formatDateTimeValue(timeFrom, timeFormat),
                timeTo = time?.[`time-to`],
                timeToFormat =
                  !!timeTo && formatDateTimeValue(timeTo, timeFormat);
              timeFrom &&
                timeTo &&
                columnHeaders.push({
                  index: dateindx,
                  timeIndex: timeindx,
                  date: date,
                  dateFormat: formatDate,
                  timeFrom,
                  timeTo,
                  timeFromFormat,
                  timeToFormat,
                  selectedAmount: allData?.length || 0,
                  selected: false,
                  reserved: false,
                  timeString: `${timeFromFormat} - ${timeToFormat}`,
                });
            });
          }
          amountOfHeaders++;
        });

        if (amountOfHeaders == appointments?.length) {

          // sort Headers
          // columnHeaders = columnHeaders.sort(
          //   (a, b) => a?.date < b?.date && a?.timeFrom < b?.timeFrom,
          // );
          columnHeaders = columnHeaders.sort((a, b) => {
            if ( (a?.date < b?.date) && (a?.timeFrom < b?.timeFrom) ) return -1;
            else if ( (a?.date > b?.date) && (a?.timeFrom > b?.timeFrom) ) return 1;
            return 0;
          });
  
      
          allData?.map((item, i) => {
            getAdminAnswers(item, i), // get all admin Answers
            generateRowCell(columnHeaders, item, i) // generate Row Cell
          });
  
        }
        resultTable = {
          ...resultTable,
          // columnHeaders.sort(
          //   (a, b) => a?.date < b?.date && a?.timeFrom < b?.timeFrom,
          // )
          columnHeaders: columnHeaders.sort((a, b) => {
            if ( (a?.date < b?.date) && (a?.timeFrom < b?.timeFrom) ) return -1;
            else if ( (a?.date > b?.date) && (a?.timeFrom > b?.timeFrom) ) return 1;
            return 0;
          }),
          rowCell,
        };
  
        // setCurrentResult(rowAdminAnswers);
        setGeneratedData(resultTable);
        return resultTable;
        }

    },
    [values, currentResult, planStatus, type],
  );

  // Regenerate Plan Headers
  const reUpdateHeaders = useCallback(
    (results, rowCell) => {
      let newHeaders = generatedData?.columnHeaders || [];

      const bodyRows = rowCell || generatedData?.rowCell;
      newHeaders =
        generatedData?.columnHeaders?.map((head, headIndex) => {
          let currentHead = { ...head };
          let currentReserved = 0;
          let currentSelected = 0;

          let isSelected =
            results?.length > 0 &&
            results?.find(
              slotData =>
                head?.date == slotData?.slot?.date &&
                head?.timeFrom == slotData?.slot?.timeFrom &&
                head?.timeTo == slotData?.slot?.timeTo,
            );

          const rows = bodyRows?.map((row, rowIndex) => {
            row?.slots?.map((slot, slotIndex) => {
              if (slotIndex == headIndex && slot?.reserved) {
                currentReserved++;
              }
              if (slotIndex == headIndex && !slot?.reserved && slot?.selected) {
                currentSelected++;
              }
              return slot;
            });
            return row;
          });

          const totalAmount =
            values?.length - currentReserved - currentSelected;

          if (!!isSelected) {
            currentHead = {
              ...currentHead,
              selected: true,
              selectedAmount: currentReserved >= 0 ? currentReserved : 0,
            };
          } else {
            currentHead = {
              ...currentHead,
              selected: false,
              selectedAmount: currentReserved >= 0 ? currentReserved : 0,
            };
          }

          return currentHead;
        }) || [];
      return newHeaders;
    },
    [values, generatedData],
  );

  // Regenerate Plan Body
  const reUpdateBody = useCallback(
    results => {
      let newRows = generatedData?.rowCell || [];

      newRows = generatedData?.rowCell?.map((row, rowInx) => {
        let currentRow = row;

        const rowHasResult = results?.find(res => res?.student == row?.student);
        const hasChosen = currentRow?.slots?.find(slot => slot?.chosen);
        if (!!rowHasResult) {
          const slots = currentRow?.slots?.map(slot => {
            let currentSlot = { ...slot, selected: true, chosen: false };
            if (
              rowHasResult?.slot?.date == slot?.date &&
              rowHasResult?.slot?.timeFrom == slot?.timeFrom &&
              rowHasResult?.slot?.timeTo == slot?.timeTo
            ) {
              currentSlot.selected = true;
              currentSlot.chosen = true;
            }
            return currentSlot;
          });
          currentRow = {
            ...currentRow,
            slots: slots,
          };
        } else {
          const slots = currentRow?.slots?.map((slot, slotInx) => {
            let currentSlot = slot;
            const selectedSlot = results.find(
              res =>
                res?.slot?.date == slot?.date &&
                res?.slot?.timeFrom == slot?.timeFrom &&
                res?.slot?.timeTo == slot?.timeTo,
            );

            if (!!!hasChosen && !!selectedSlot) {
              currentSlot = { ...currentSlot, selected: true, chosen: false };
            } else if (!!hasChosen && !!selectedSlot) {
              currentSlot = { ...currentSlot, selected: true, chosen: false };
            } else {
              currentSlot = { ...currentSlot, selected: false, chosen: false };
            }

            return currentSlot;
          });

          currentRow = {
            ...currentRow,
            slots: slots,
          };
        }

        return currentRow;
      });

      return newRows;
    },
    [generatedData],
  );

  // Save Results by Rules
  const reUpdateResults = useCallback(
    updatedCell => {
      let results = currentResult || [];
      // generate admin results
      const hasResultIndex = currentResult?.findIndex(
        res =>
          res?.student == updatedCell?.student &&
          res?.slot?.timeFrom == updatedCell?.slot?.timeFrom &&
          res?.slot?.timeTo == updatedCell?.slot?.timeTo &&
          res?.slot?.date == updatedCell?.slot?.date,
      );

      const hasThisSlot = hasResultIndex != -1;

      // if we click on the same slot
      if (hasThisSlot) {
        if (updatedCell?.slot?.chosen) {
          results[hasResultIndex] = updatedCell;
        } else if (!updatedCell?.slot?.chosen) {
          results = results?.filter((el, inx) => inx != hasResultIndex) || [];
        }

      }

      // if we clicked on another slot in the plan
      else {
        const sameStudentIndx = results?.findIndex(
          res => res?.student == updatedCell?.student,
        );
        const sameSlotIndx = results?.findIndex(
          res =>
            res?.slot?.date == updatedCell?.slot?.date &&
            res?.slot?.timeFrom == updatedCell?.slot?.timeFrom &&
            res?.slot?.timeTo == updatedCell?.slot?.timeTo,
        );

        if (sameSlotIndx == -1) {

          if (sameStudentIndx == -1) {
            if (updatedCell?.slot?.chosen) {
              results = [...results, updatedCell];
            } else {
              results = results?.filter((el, inx) => inx != sameStudentIndx) || [];
            }
          } else {
            if (updatedCell?.slot?.chosen) {
              results = results?.map((el, inx) =>
                sameStudentIndx == inx ? updatedCell : el,
              );
            } else {
              results = results?.filter((el, inx) => inx != sameStudentIndx) || [];
            }
          }

        } else {
          
          if (sameStudentIndx == -1) {
            // TODO: does this code work at all ?
            if (updatedCell?.slot?.chosen) {
              // let newRes = results?.filter(item => item?.sameSlot != updatedCell?.sameSlot);
              const newRes = results?.map((item, inx) => {
                return sameSlotIndx == inx ? updatedCell : item
              }); 
              results = newRes;
            } else {
              results = results?.filter((item, inx) => sameSlotIndx == inx);
            }
          }
          // INFO: code for deselect another choose if we click on chosen slot
          else {
            let newResult = results?.filter(
              (el, inx) => {
                const isSame = el?.slot?.data == updatedCell?.slot?.data &&
                  el?.slot?.timeFrom == updatedCell?.slot?.timeFrom &&
                  el?.slot?.timeTo == updatedCell?.slot?.timeTo;
                return !isSame;
              });

            if (updatedCell?.slot?.chosen) {
              newResult = results?.map((el, inx) => {
                const isSame = el?.slot?.data == updatedCell?.slot?.data &&
                  el?.slot?.timeFrom == updatedCell?.slot?.timeFrom &&
                  el?.slot?.timeTo == updatedCell?.slot?.timeTo;
                if (inx == sameStudentIndx) {
                  return updatedCell;
                } else {
                  return !isSame ? el : false;
                }
              }).filter(el => !!el);
            } else {
              newResult = results?.filter(
                (item, inx) => item?.student != updatedCell?.student,
              );
            }
            results = newResult; 
          }
        }
      }

      const finalResult = results?.length <= values?.length ? results : [];
      setCurrentResult([...finalResult]);
      return finalResult;
    },
    [values, currentResult],
  );

  // Callback for saving of Slot
  const onUpdateHandle = useCallback(
    (rowData, slotIndex, rowlIndex) => {
      const { slots, ...restData } = rowData;

      const slot = slots[slotIndex];
      let updatedCell = {
        ...restData,
        slot: { ...slot, selected: true, chosen: true },
        slotIndex,
        rowlIndex,
      };
      if (!!slot?.chosen) {
        updatedCell = {
          ...restData,
          slot: { ...slot, selected: false, chosen: false },
          slotIndex,
          rowlIndex,
        };
      }
      
      // generate admin results
      reUpdateResults(updatedCell);
    },
    [reUpdateResults],
  );

  
  const renderPlanHeader = useMemo(() => { 
    return (
      <div className="plan-header">
        {/* Top Headers */}
        <div className="top-headers">
          <div className="label">{t("labels.student")}</div>
          {generatedData?.columnHeaders?.map((h, hi) => {
            return (
              <div
                key={`plan-head-${hi}`}
                className={`head ${h?.selected ? "selected" : ""}`}
              >
                {h?.dateFormat} <br /> {h?.timeString}
              </div>
            );
          })}
        </div>
        {/* Bottom Headers */}
        <div className="bottom-headers">
          <div className="col label">{t("common.availability")}</div>
          {generatedData?.columnHeaders?.map((h, hi) => (
            <div key={`plan-bottom-head-${hi}`} className="head">
              {h?.selectedAmount || 0}
            </div>
          ))}
        </div>
      </div>
    );
  }, [generatedData?.columnHeaders, i18n?.language]);

  const renderPlanBody = useMemo(() => { 
    return (
      <div className="plan-body">
        {generatedData?.rowCell?.map((row, rowlIndex) => {
          let hasChosen = row?.slots?.find(slot => slot?.chosen);

          return (
            <div
              key={`plan-row-${rowlIndex}`}
              className={classNames(
                "plan-row",
                hasChosen && "disabled",
              )}
            >
              <div className="row-label">
                <div className="row-studnet">
                  {`${row?.lastname} ${row?.firstname}`}
                </div>
                <div className="row-availability">
                  {row?.reservedAmount || 0}
                </div>
              </div>
              {row?.slots?.map((slot, slotIndex) => {
                return (
                  <div
                    key={`plan-row-slot-${rowlIndex}-${slotIndex}`}
                    className={classNames(
                      "row-cell",
                      slot?.reserved && "reserved",
                      slot?.selected && "disabled",
                      slot?.chosen && "selected",
                    )}
                  >
                    <Tooltip
                      placement="left"
                      trigger="hover"
                      title={(
                        <>
                          {slot?.dateFormat}<br/>
                          {slot?.timeString}
                        </>
                      )}
                      getPopupContainer={trigger => trigger.parentElement}
                    >
                      <Button
                        className="row-cell-button"
                        onClick={() =>
                          onUpdateHandle(row, slotIndex, rowlIndex)
                        }
                      >
                        {slot?.chosen && <StarFilled />}
                      </Button>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    )
  }, [generatedData?.rowCell, onUpdateHandle, i18n?.language]);

  return (
    !!values && (
      <div {...rest} className={childClassNames}>
        <div className="tcf-fb-group-preview__content">
          <div className={`appointments-plan ${planStatus}`}>
            <ErrorBoundary>

              {/* Plan Headers */}
              {renderPlanHeader}

              {/* Plan Body */}
              {renderPlanBody}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    )
  );
}

FBAppointmentsPlan.defaultProps = {
  type: "appointments",
};
FBAppointmentsPlan.propTypes = {
  values: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.bool,
    PropTypes.array,
  ]),
};

export default memo(FBAppointmentsPlan);
